import { useMemo } from 'react';
import type {
  GroupedPublishedCompetition,
  GroupedPublishedEvent,
  GroupedPublishedSport,
  GroupedPublishedTournament,
  Market,
  PublishedEventListItem,
  PublishedEventParticipants,
  TopMarketOutcomeType,
  TopMarketTypeDisplay,
} from '../@types';
import { QUERY_KEYS } from '../constants';
import { API_ROUTES } from '../constants/apiRoutes';
import { useGroupedPublishedEvents } from '../queries';
import { getDisplayName } from '../utils';

export type FeaturedGroupedPublishedEvent = GroupedPublishedEvent & {
  participants: PublishedEventParticipants;
  topMarkets: Market[];
};

export type GroupedFeaturedEventGroup = {
  id: string;
  title: string;
  link: string;
  sport: GroupedPublishedSport;
  competition: GroupedPublishedCompetition;
  tournament: GroupedPublishedTournament;
  events: FeaturedGroupedPublishedEvent[];
  topMarketTypes: TopMarketTypeDisplay[];
  outcomeTypes?: TopMarketOutcomeType[];
};

const hasFeaturedEventData = (
  event: GroupedPublishedEvent
): event is FeaturedGroupedPublishedEvent =>
  !!event.participants?.home && !!event.participants.away && !!event.topMarkets?.[0];

export const getGroupedFeaturedTournamentTitle = (tournament: GroupedPublishedTournament) =>
  tournament.parentTournamentName ?? getDisplayName(tournament);

export const getGroupedFeaturedTournamentLink = (sportId: string, tournamentId: string) =>
  `/sports/${sportId}/tournaments/${tournamentId}`;

export type FeaturedEventItem = {
  group: GroupedFeaturedEventGroup;
  event: FeaturedGroupedPublishedEvent;
};

/**
 * Flattens featured groups into a single chronologically-ordered strip of {group, event}
 * items — interleaving events across tournament groups by start date. This is the shared
 * source of truth every grouped-featured carousel renders; the carousels own only their
 * shell markup and the empty-state guard.
 */
export const getSortedFeaturedEventItems = (groups: GroupedFeaturedEventGroup[]): FeaturedEventItem[] =>
  groups
    .flatMap((group) => group.events.map((event) => ({ group, event })))
    .sort((a, b) => Date.parse(a.event.startDate) - Date.parse(b.event.startDate));

/**
 * Normalizes a grouped featured event into the shape the standard event components (EventsTable,
 * EventCard) are typed against. The grouped feed carries event-level data on the event and the
 * tournament/competition/sport context on the group, so we graft the group's context back onto
 * each event. The cast is the single boundary where the grouped feed's Pick<> types are widened to
 * the full PublishedEventListItem — consumers only read the fields populated here.
 */
export const toPublishedEventListItem = (
  group: GroupedFeaturedEventGroup,
  event: FeaturedGroupedPublishedEvent
): PublishedEventListItem =>
  ({
    ...event,
    isActive: true,
    tournamentId: group.tournament.id,
    competition: group.competition,
    tournament: { ...group.tournament, sport: group.sport, competition: group.competition },
  }) as unknown as PublishedEventListItem;

export const getGroupedFeaturedEventGroups = (sports: GroupedPublishedSport[]): GroupedFeaturedEventGroup[] =>
  sports.flatMap((sport) =>
    sport.competitions.flatMap((competition) =>
      competition.tournaments
        .map((tournament): GroupedFeaturedEventGroup | null => {
          const events = tournament.events.filter(hasFeaturedEventData);

          if (!events.length) {
            return null;
          }

          const topMarketTypes = sport.topMarketTypes ?? [];
          const outcomeTypes = topMarketTypes[0]?.outcomeTypes;

          return {
            id: tournament.id,
            title: getGroupedFeaturedTournamentTitle(tournament),
            link: getGroupedFeaturedTournamentLink(sport.id, tournament.id),
            sport,
            competition,
            tournament,
            events,
            topMarketTypes,
            ...(outcomeTypes ? { outcomeTypes } : {}),
          };
        })
        .filter((group): group is GroupedFeaturedEventGroup => !!group)
    )
  );

export const useGroupedFeaturedContent = () => {
  const { data: featuredTournamentsData, isLoading: isFeaturedTournamentsLoading } = useGroupedPublishedEvents({
    queryKey: [QUERY_KEYS.featuredEvents, 'grouped-featured-tournaments'],
    endpoint: API_ROUTES.EVENTS.PUBLISHED_GROUPED_FEATURED_TOURNAMENTS,
    queryParams: {
      limit: 50,
      count: false,
    },
  });
  const { data: featuredEventsData, isLoading: isFeaturedEventsLoading } = useGroupedPublishedEvents({
    queryKey: [QUERY_KEYS.featuredEvents, 'grouped'],
    queryParams: {
      limit: 50,
      featured: true,
      sort: 'startDate',
      count: false,
    },
  });

  const featuredTournamentGroups = useMemo(
    () => getGroupedFeaturedEventGroups(featuredTournamentsData?.items ?? []),
    [featuredTournamentsData?.items]
  );
  const featuredEventGroups = useMemo(
    () => getGroupedFeaturedEventGroups(featuredEventsData?.items ?? []),
    [featuredEventsData?.items]
  );

  return {
    featuredEventGroups,
    featuredTournamentGroups,
    isFeaturedEventsLoading,
    isFeaturedTournamentsLoading,
  };
};
